import React, { useState, useRef, useEffect, useMemo } from "react";
import { ArrowLeft, Save, UploadCloud, Camera } from "lucide-react";
import {
  reviewSafetyObservationByChecker,
  reviewSafetyObservationByContractorChecker,
} from "../../../../../api";
import { showToast } from "../../../../../utils/toast";
import SafetyImageAnnotationModal from "../../SafetyImageAnnotationModal";
import SafetyCameraCaptureModal from "../../SafetyCameraCaptureModal";
import SafetyResponsiveSignatureCanvas from "../../../SafetyResponsiveSignatureCanvas";

import { Check, X as XIcon } from "lucide-react";
import RiskMatrixModal from "./RiskMatrixModal";
import { resolveMediaUrl } from "../../../../../lib/utils";
import SafetyObservationSignOffSection from "../../SafetyObservationSignOffSection";
import ObservationReviewCard from "./ObservationReviewCard";
import {
  buildAttemptReviewEvents,
  hasCurrentAttemptContractorReview,
  parseRejectionHistory,
} from "./observationReviewHelpers";

const formatObservationInspectionDate = (dateVal) => {
  if (!dateVal) return "—";
  try {
    const raw = String(dateVal).trim();
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      return raw;
    }
    const datePart = raw.split("T")[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [y, m, d] = datePart.split("-");
      return `${d}-${m}-${y}`;
    }
    const d = new Date(dateVal);
    if (!Number.isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    }
    return String(dateVal);
  } catch (e) {
    return String(dateVal || "—");
  }
};

const ReadOnlyHeaderField = ({ label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
      {label}
    </p>
    <p className="mt-1 break-words text-sm font-semibold text-slate-800">
      {String(value || "").trim() || "—"}
    </p>
  </div>
);

const PhotoUploadArea = ({
  id,
  file,
  onFileChange,
  onRemove,
  label,
  onAnnotate,
}) => {
  const inputRef = useRef(null);
  const previewUrl = file ? URL.createObjectURL(file) : null;

  const handleRemove = () => {
    if (inputRef.current) inputRef.current.value = "";
    onRemove?.();
  };

  return (
    <div className="mt-3">
      <p className="mb-1.5 text-xs font-medium text-slate-500">{label}</p>
      {previewUrl ? (
        <div className="flex items-center gap-4">
          <div className="relative inline-block">
            <img
              src={previewUrl}
              alt="Preview"
              className="h-20 w-20 rounded-lg border border-slate-200 object-cover shadow-sm"
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow transition-colors hover:bg-red-600"
            >
              <XIcon className="h-3 w-3" />
            </button>
          </div>
          {onAnnotate && (
            <button
              type="button"
              onClick={onAnnotate}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-sm"
            >
              Edit / Annotate
            </button>
          )}
        </div>
      ) : (
        <label
          htmlFor={`photo-upload-${id}`}
          className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-xs text-slate-500 transition-colors hover:border-blue-400 hover:bg-blue-50"
        >
          <UploadCloud className="h-4 w-4" />
          <span>Click to attach photo</span>
        </label>
      )}
      <input
        ref={inputRef}
        id={`photo-upload-${id}`}
        type="file"
        accept="image/*"
        onChange={(e) => onFileChange(e.target.files?.[0])}
        className="hidden"
      />
    </div>
  );
};

export default function ReviewSafetyObservation({ detail, onBack, onSuccess }) {
  const meta = useMemo(() => {
    if (!detail?.report_header_meta) return {};
    if (typeof detail.report_header_meta === "object") return detail.report_header_meta;
    try {
      return JSON.parse(detail.report_header_meta);
    } catch (e) {
      return {};
    }
  }, [detail?.report_header_meta]);

  const [submitting, setSubmitting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectPhotos, setRejectPhotos] = useState([]);
  const [rejectCameraOpen, setRejectCameraOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [finalReportCommentText, setFinalReportCommentText] = useState("");
  const [photoComments, setPhotoComments] = useState({}); // ID -> comment string
  const [showAnnotationModal, setShowAnnotationModal] = useState(false);
  const [annotatingIndex, setAnnotatingIndex] = useState(null);

  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [signatureAction, setSignatureAction] = useState(null);
  const [showRiskMatrix, setShowRiskMatrix] = useState(false);

  // Final Image Selection Modal State
  const [showFinalImageModal, setShowFinalImageModal] = useState(false);
  const [availableFinalImages, setAvailableFinalImages] = useState([]);
  const [selectedFinalImage, setSelectedFinalImage] = useState(null);
  const [selectedFinalComments, setSelectedFinalComments] = useState([]);
  const [pendingApprovalPayload, setPendingApprovalPayload] = useState(null);
  const sigPadRef = React.useRef(null);

  // Enlarged image preview (lightbox) state
  const [enlargedImage, setEnlargedImage] = useState(null);

  const normalizedStatus = String(detail?.status || "")
    .trim()
    .toUpperCase();

  const isInternalCheckerReview =
    normalizedStatus === "PENDING_CHECKER";

  const isContractorCheckerReview =
    normalizedStatus === "PENDING_CONTRACTOR_CHECKER";

  const currentAttempt = (() => {
    const attempts = Array.isArray(detail?.attempts)
      ? detail.attempts
      : [];

    if (!attempts.length) return null;

    const currentAttemptNo = Number(
      detail?.current_attempt_no || 0,
    );

    return (
      attempts.find(
        (attempt) =>
          Number(attempt?.attempt_no) === currentAttemptNo,
      ) ||
      attempts[attempts.length - 1] ||
      null
    );
  })();

  const closerPhotographs = Array.isArray(
    currentAttempt?.safety_officer_photographs,
  )
    ? currentAttempt.safety_officer_photographs
    : [];

  const allAttempts = Array.isArray(detail?.attempts)
    ? [...detail.attempts].sort(
        (a, b) => Number(a?.attempt_no || 0) - Number(b?.attempt_no || 0),
      )
    : [];

  const previousAttempts = allAttempts.filter(
    (attempt) =>
      Number(attempt?.attempt_no || 0) <
      Number(detail?.current_attempt_no || 0),
  );

  const rejectionHistory = (() => {
    try {
      if (Array.isArray(detail?.rejection_history)) {
        return detail.rejection_history;
      }

      const parsed = JSON.parse(detail?.rejection_history || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();

  const submittedMakerName =
    currentAttempt?.claimed_by_safety_officer_name ||
    detail?.maker_name ||
    "";

  const makerSubmissionComment =
    currentAttempt?.safety_officer_comment || "";

  const makerSubmittedAt =
    currentAttempt?.safety_officer_submitted_at ||
    detail?.safety_officer_submitted_at ||
    null;

  // Are all closer-photo comments identical to each other (and to the maker's
  // overall submission remark)? If so we only need to show the remark once,
  // instead of repeating the same text under every photo.
  const closerPhotoComments = closerPhotographs
    .map((p) => (p?.comment || "").trim())
    .filter(Boolean);
  const uniqueCloserPhotoComments = [...new Set(closerPhotoComments)];
  const shareOneCommonComment = uniqueCloserPhotoComments.length <= 1;

  const getPhotoPath = (photo) =>
    photo?.annotated_image_url ||
    photo?.annotated_image ||
    photo?.image_url ||
    photo?.image ||
    null;

  const formatDateTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getAttemptRejections = (attemptNo) =>
    rejectionHistory.filter(
      (item) => Number(item?.attempt_no || 0) === Number(attemptNo || 0),
    );

  const renderPhotoCollection = (
    photos,
    {
      emptyText = "No photograph attached.",
      borderClass = "border-slate-200",
      backgroundClass = "bg-white",
      label = "Photo",
    } = {},
  ) => {
    const list = Array.isArray(photos) ? photos : [];

    if (!list.length) {
      return (
        <p className="text-sm italic text-slate-500">
          {emptyText}
        </p>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {list.map((photo, index) => {
          const photoPath = getPhotoPath(photo);

          return (
            <div
              key={photo?.id || index}
              className={`rounded-lg border ${borderClass} ${backgroundClass} p-3 shadow-sm`}
            >
              <div className="mb-2 flex items-center justify-between border-b border-slate-100 pb-1.5">
                <span className="text-xs font-bold text-slate-700">
                  Photograph #{index + 1}
                </span>
              </div>
              {photoPath ? (
                <img
                  src={resolveMediaUrl(photoPath)}
                  alt={`${label} ${index + 1}`}
                  onClick={() =>
                    setEnlargedImage({
                      src: resolveMediaUrl(photoPath),
                      label: `${label} ${index + 1}`,
                    })
                  }
                  className="h-40 w-full cursor-zoom-in rounded-lg border border-slate-200 bg-slate-50 object-contain transition hover:opacity-90"
                />
              ) : (
                <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-xs text-slate-500">
                  Image unavailable
                </div>
              )}

              {photo?.comment && (
                <div className="mt-2 rounded-lg border border-slate-100 bg-slate-50 p-2.5 text-sm text-slate-700">
                  <strong>Photo Remark:</strong>{" "}
                  {photo.comment}
                </div>
              )}

              {photo?.checker_comment && (
                <div className="mt-2 rounded-lg border border-red-100 bg-red-50 p-2.5 text-sm text-red-700">
                  <strong>Checker Comment:</strong>{" "}
                  {photo.checker_comment}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderAttemptHistory = (attempt) => {
    const attemptNo = Number(attempt?.attempt_no || 0);
    const makerPhotos = Array.isArray(attempt?.safety_officer_photographs)
      ? attempt.safety_officer_photographs
      : [];

    const reviewEvents = buildAttemptReviewEvents(attempt, rejectionHistory, detail);

    const isAttemptRejected = reviewEvents.some((e) => e.rawDecision === "REJECTED");
    const isAttemptApproved = reviewEvents.some((e) => e.rawDecision === "APPROVED");
    const displayBadgeDecision = isAttemptRejected
      ? "REJECTED"
      : isAttemptApproved
        ? "APPROVED"
        : "PENDING";

    return (
      <div
        key={attempt?.id || attemptNo}
        className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
          <div>
            <p className="text-sm font-bold text-slate-800">
              Attempt {attemptNo}
            </p>
            <p className="text-xs text-slate-500">
              Rectification and Review Audit Trail
            </p>
          </div>

          <span
            className={`rounded-full px-3 py-1 text-xs font-bold ${
              displayBadgeDecision === "REJECTED"
                ? "bg-red-100 text-red-700"
                : displayBadgeDecision === "APPROVED"
                  ? "bg-green-100 text-green-700"
                  : "bg-amber-100 text-amber-700"
            }`}
          >
            {displayBadgeDecision}
          </span>
        </div>

        <div className="space-y-4 p-4">
          {/* Maker Rectification Submission */}
          <div className="rounded-xl border border-orange-200 bg-orange-50/70 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-orange-200/60 pb-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wide text-orange-800">
                    Maker Rectification
                  </p>
                  <p className="mt-0.5 text-sm font-bold text-slate-800">
                    {attempt?.claimed_by_safety_officer_name ||
                      detail?.maker_name ||
                      "Maker"}
                  </p>
                </div>

                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Submitted At
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-slate-700">
                    {formatDateTime(attempt?.safety_officer_submitted_at)}
                  </p>
                </div>
              </div>

              <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-700">
                SUBMITTED
              </span>
            </div>

            <div className="mt-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Maker Remarks
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">
                {attempt?.safety_officer_comment ||
                  "No Maker remarks provided."}
              </p>
            </div>

            <div className="mt-3">
              {renderPhotoCollection(makerPhotos, {
                emptyText: "No Maker photograph attached for this attempt.",
                borderClass: "border-orange-100",
                backgroundClass: "bg-white",
                label: "Maker photograph",
              })}
            </div>

            {attempt?.safety_officer_signature && (
              <div className="mt-3 flex items-center justify-between border-t border-orange-200/60 pt-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Maker Signature
                  </p>
                </div>
                <div className="rounded border border-slate-200 bg-white p-1 shadow-xs">
                  <img
                    src={resolveMediaUrl(attempt.safety_officer_signature)}
                    alt="Maker Signature"
                    className="max-h-12 max-w-[140px] object-contain"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Review Events in Chronological Order */}
          {reviewEvents.map((event) => (
            <ObservationReviewCard
              key={event.id}
              reviewerRole={event.reviewerRole}
              reviewerName={event.reviewerName}
              decision={event.decision}
              remarks={event.remarks}
              reviewedAt={event.reviewedAt}
              signature={event.signature}
              rejectionPhotos={event.rejectionPhotos}
              onImageClick={(src, label) => setEnlargedImage({ src, label })}
            />
          ))}
        </div>
      </div>
    );
  };

  if (!detail) return null;

  let hazardsArray = [];
  try {
    if (detail.hazard_categories) {
      hazardsArray = JSON.parse(detail.hazard_categories);
      if (!Array.isArray(hazardsArray)) {
        hazardsArray = [String(detail.hazard_categories)];
      }
    }
  } catch (e) {
    if (detail.hazard_categories) {
      hazardsArray = [String(detail.hazard_categories)];
    }
  }

  const HAZARDS_OPTIONS = [
    "1. Physical Hazard",
    "2. Biological Hazard",
    "3. Chemical Hazard",
    "4. Mechanical Hazard",
    "5. Ergonomical Hazard",
    "6. Environmental Hazard",
    "7. Fire/Explosion Hazard",
    "8. Electrical Hazard",
    "9. Psychological Hazard",
    "10. Other Hazards",
  ];

  const extractUniqueRemarks = (photo) => {
    const r1 = String(photo?.maker_submission_remark || "").trim();
    const r2 = String(photo?.photo_remark || "").trim();

    if (r1 && r2) {
      if (r1.toLowerCase() === r2.toLowerCase()) {
        return [r1];
      }
      const lines1 = r1.split("\n").map((l) => l.trim()).filter(Boolean);
      const lines2 = r2.split("\n").map((l) => l.trim()).filter(Boolean);
      const set = new Set();
      const result = [];
      for (const l of [...lines1, ...lines2]) {
        if (!set.has(l.toLowerCase())) {
          set.add(l.toLowerCase());
          result.push(l);
        }
      }
      return result;
    }
    return [r1 || r2].filter(Boolean);
  };

  const handleApprove = () => {
    if (isContractorCheckerReview) {
      setSignatureAction("approve");
      setIsSignatureModalOpen(true);
      return;
    }

    // Target the active/current rectification attempt
    const activeAttempt =
      currentAttempt ||
      (allAttempts.length > 0 ? allAttempts[allAttempts.length - 1] : null);

    let rawPhotos =
      Array.isArray(activeAttempt?.safety_officer_photographs) &&
      activeAttempt.safety_officer_photographs.length > 0
        ? activeAttempt.safety_officer_photographs.map((p) => ({
            ...p,
            attempt_no: p?.attempt_no || activeAttempt?.attempt_no || null,
            maker_name:
              activeAttempt?.claimed_by_safety_officer_name ||
              detail?.maker_name ||
              "Maker",
            maker_submitted_at:
              activeAttempt?.safety_officer_submitted_at ||
              p?.uploaded_at ||
              null,
            maker_submission_remark:
              activeAttempt?.safety_officer_comment || "",
            photo_remark: p?.comment || "",
          }))
        : [];

    // Fallback: If active attempt has no photos, check all attempts from latest to oldest
    if (!rawPhotos.length && allAttempts.length > 0) {
      for (const att of [...allAttempts].reverse()) {
        if (
          Array.isArray(att?.safety_officer_photographs) &&
          att.safety_officer_photographs.length > 0
        ) {
          rawPhotos = att.safety_officer_photographs.map((p) => ({
            ...p,
            attempt_no: p?.attempt_no || att?.attempt_no || null,
            maker_name:
              att?.claimed_by_safety_officer_name ||
              detail?.maker_name ||
              "Maker",
            maker_submitted_at:
              att?.safety_officer_submitted_at ||
              p?.uploaded_at ||
              null,
            maker_submission_remark: att?.safety_officer_comment || "",
            photo_remark: p?.comment || "",
          }));
          break;
        }
      }
    }

    // Fallback: If still empty, check detail.closer_photographs
    if (!rawPhotos.length && Array.isArray(detail?.closer_photographs)) {
      rawPhotos = detail.closer_photographs.map((p) => ({
        ...p,
        attempt_no: p?.attempt_no || detail?.current_attempt_no || null,
        maker_name: detail?.maker_name || "Maker",
        maker_submitted_at:
          detail?.safety_officer_submitted_at || p?.uploaded_at || null,
        maker_submission_remark: detail?.safety_officer_comment || "",
        photo_remark: p?.comment || "",
      }));
    }

    // Strictly deduplicate photos by unique id or photo path
    const seenPhotoKeys = new Set();
    const makerPhotos = [];
    rawPhotos.forEach((photo, pIdx) => {
      const photoPath = getPhotoPath(photo);
      const key = photo?.id
        ? `id-${photo.id}`
        : photoPath
          ? `path-${photoPath}`
          : `idx-${pIdx}`;

      if (!seenPhotoKeys.has(key)) {
        seenPhotoKeys.add(key);
        makerPhotos.push({
          ...photo,
          photo_index: makerPhotos.length + 1,
          type: "closer",
        });
      }
    });

    if (!makerPhotos.length) {
      showToast(
        "No Maker rectification photograph is available for final report selection.",
        "error",
      );
      return;
    }

    setAvailableFinalImages(makerPhotos);
    const initialPhoto = makerPhotos.length === 1 ? makerPhotos[0] : null;
    setSelectedFinalImage(initialPhoto);
    const initialComments = initialPhoto ? extractUniqueRemarks(initialPhoto) : [];
    setSelectedFinalComments(initialComments);

    const initialReportComment =
      (initialPhoto && photoComments[initialPhoto.id]) ||
      (initialComments.length > 0 ? initialComments.join("\n") : (approvalRemarks || ""));
    setFinalReportCommentText(initialReportComment);

    setShowFinalImageModal(true);
  };

  const handleRejectGalleryChange = (e) => {
    if (e.target.files?.length > 0) {
      const newFiles = Array.from(e.target.files).map((f) => {
        f.comment = "";
        return f;
      });
      setRejectPhotos((prev) => [...prev, ...newFiles]);
    }
    e.target.value = "";
  };

  const handleRejectCameraCapture = (capturedFile) => {
    if (!capturedFile) return;
    capturedFile.comment = "";
    const nextIdx = rejectPhotos.length;
    setRejectPhotos((prev) => [...prev, capturedFile]);
    setRejectCameraOpen(false);
    setAnnotatingIndex(nextIdx);
    setShowAnnotationModal(true);
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      showToast("Please provide a reason for rejection.", "error");
      return;
    }
    setSignatureAction("reject");
    setIsSignatureModalOpen(true);
  };

  const confirmFinalImageSelection = () => {
    if (!selectedFinalImage) {
      showToast(
        "Please select the Maker photograph for the final report.",
        "error",
      );
      return;
    }

    setShowFinalImageModal(false);
    setSignatureAction("approve");
    setIsSignatureModalOpen(true);
  };

  const confirmSubmit = async (signatureFile) => {
    if (!signatureFile) {
      showToast("Please provide your signature.", "error");
      return;
    }

    if (!isInternalCheckerReview && !isContractorCheckerReview) {
      showToast(
        "This observation is not currently pending Checker review.",
        "error",
      );
      return;
    }

    setSubmitting(true);

    try {
      const payload = new FormData();

      const decision =
        signatureAction === "approve"
          ? "APPROVED"
          : "REJECTED";

      payload.append("decision", decision);
      payload.append("signature", signatureFile);

      if (decision === "REJECTED") {
        payload.append("reason", rejectReason.trim());

        // Append reject photos in canonical indexed multipart format for BOTH checker levels
        rejectPhotos.forEach((item, index) => {
          if (item?.file) {
            payload.append(`reject_photographs[${index}][image]`, item.file);
            if (item.annotatedFile) {
              payload.append(
                `reject_photographs[${index}][annotated_image]`,
                item.annotatedFile,
              );
            }
            const comment = (item.comment || "").trim();
            if (comment) {
              payload.append(`reject_photographs[${index}][comment]`, comment);
            }
          }
        });
      } else {
        if (approvalRemarks.trim()) {
          payload.append("reason", approvalRemarks.trim());
        }
      }

      if (isInternalCheckerReview) {
        if (decision === "APPROVED") {
          if (!selectedFinalImage?.id) {
            showToast(
              "Please select the Maker photograph for the final report.",
              "error",
            );
            return;
          }

          payload.append(
            "final_report_photo_id",
            String(selectedFinalImage.id),
          );

          const finalComment = (
            finalReportCommentText ||
            selectedFinalComments.filter(Boolean).join("\n")
          ).trim();

          if (finalComment) {
            payload.append(
              "final_report_comment",
              finalComment,
            );
          }
        }

        await reviewSafetyObservationByChecker(
          detail.id,
          payload,
        );
      } else {
        await reviewSafetyObservationByContractorChecker(
          detail.id,
          payload,
        );
      }

      if (decision === "APPROVED") {
        showToast(
          isContractorCheckerReview
            ? "Observation approved and sent to Internal Checker for final review."
            : "Observation approved successfully!",
          "success",
        );
      } else {
        showToast(
          "Observation Rejected Successfully!",
          "success",
        );
      }

      if (onSuccess) onSuccess();
    } catch (err) {
      const responseData = err?.response?.data;

      const message =
        responseData?.detail ||
        responseData?.reason?.[0] ||
        responseData?.final_report_photo_id?.[0] ||
        responseData?.status?.[0] ||
        responseData?.assignment?.[0] ||
        responseData?.message ||
        `Failed to ${signatureAction} observation.`;

      showToast(message, "error");
    } finally {
      setSubmitting(false);
      setSignatureAction(null);
    }
  };

  return (
    <div className="mx-auto max-w-5xl pb-12 px-3 sm:px-4 md:px-6">
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            onClick={onBack}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white shadow-sm transition hover:bg-slate-100"
          >
            <ArrowLeft className="h-5 w-5 text-slate-700" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
              Review Safety Observation #{detail?.observation_no ?? detail?.id}
            </h1>
            <p className="text-xs sm:text-sm text-slate-500">
              Please review the rectification submitted by the Maker.
              {(detail?.forwarded_at || currentAttempt?.safety_officer_submitted_at || detail?.safety_officer_submitted_at || detail?.checker_reviewed_at || detail?.updated_at) && (
                <span className="ml-2 font-medium text-slate-600">
                  · Forwarded: {formatDateTime(detail?.forwarded_at || currentAttempt?.safety_officer_submitted_at || detail?.safety_officer_submitted_at || detail?.checker_reviewed_at || detail?.updated_at)}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Inspection Details Header (Safety Inspection Theme) */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm mb-6">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-slate-900">
            Inspection Details
          </h3>
          <p className="mt-1 text-xs text-slate-500">
            Complete the inspection details below before answering the checklist questions. Required fields marked with * must be filled and will be included in the generated report.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* Row 1 */}
          <ReadOnlyHeaderField
            label="FORMAT NO."
            value={detail?.format_no || meta?.format_no || "ADL-OH&S-F012"}
          />

          <ReadOnlyHeaderField
            label="REVISION NO."
            value={detail?.revision_no || meta?.revision_no || "R0"}
          />

          <ReadOnlyHeaderField
            label="ISSUED DATE"
            value={
              (detail?.issued_date || meta?.issued_date)
                ? (formatObservationInspectionDate(detail?.issued_date || meta?.issued_date) !== "—"
                    ? formatObservationInspectionDate(detail?.issued_date || meta?.issued_date)
                    : "01-09-2025")
                : "01-09-2025"
            }
          />

          {/* Row 2 */}
          <ReadOnlyHeaderField
            label="REVISION DATE"
            value={
              detail?.revision_date || meta?.revision_date
                ? formatObservationInspectionDate(detail?.revision_date || meta?.revision_date)
                : "—"
            }
          />

          <ReadOnlyHeaderField
            label="PROJECT"
            value={
              detail?.project_name ||
              detail?.project?.name ||
              detail?.project_title ||
              meta?.project ||
              (detail?.project_id ? `Project ${detail.project_id}` : "—")
            }
          />

          <ReadOnlyHeaderField
            label="INSPECTION REPORT NO."
            value={
              detail?.inspection_report_no ||
              meta?.inspection_report_no ||
              detail?.report_no ||
              (detail?.id ? `AN-ADL-SHOR-${detail.id}` : "AN-ADL-SHOR-AUTO")
            }
          />

          {/* Row 3 */}
          <ReadOnlyHeaderField
            label="DATE OF INSPECTION"
            value={formatObservationInspectionDate(
              detail?.date_of_inspection ||
                meta?.date_of_inspection ||
                detail?.inspection_date ||
                detail?.created_at
            )}
          />
        </div>
      </div>

      <div className="rounded-xl border bg-white p-4 sm:p-6 shadow-sm">
        <div className="space-y-8">
          {/* Q1 */}
          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 opacity-80">
            <h3 className="font-bold text-slate-600 mb-4">
              1. WHAT UNSAFE ACT / CONDITION OBSERVED
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <select
                  className="w-full rounded-lg border-slate-300 p-2.5 text-sm bg-slate-100 cursor-not-allowed"
                  value={detail.unsafe_act_condition_category || ""}
                  disabled
                >
                  <option value="">Select Category...</option>
                  <option value="Unsafe Act">Unsafe Act</option>
                  <option value="Unsafe Condition">Unsafe Condition</option>
                </select>
              </div>
              <div>
                <input
                  type="text"
                  className="w-full rounded-lg border-slate-300 p-2.5 text-sm bg-slate-100 cursor-not-allowed"
                  value={detail.unsafe_act_condition_description || ""}
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Q2 */}
          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 opacity-80">
            <h3 className="font-bold text-slate-600 mb-4">2. LOCATION</h3>
            <div>
              <input
                type="text"
                className="w-full rounded-lg border-slate-300 p-2.5 text-sm bg-slate-200 cursor-not-allowed"
                value={detail.location_combined || ""}
                readOnly
              />
            </div>
          </div>

          {/* Q3 */}
          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 opacity-80">
            <h3 className="font-bold text-slate-600 mb-4">
              3. PHOTOGRAPH OF UNSAFE ACT / CONDITION
            </h3>
            {Array.isArray(detail.initial_photographs) && detail.initial_photographs.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {detail.initial_photographs.map((photoItem, pIdx) => {
                  const rawUrl =
                    photoItem?.annotated_image_url ||
                    photoItem?.annotated_image ||
                    photoItem?.image_url ||
                    photoItem?.image ||
                    photoItem;
                  const pUrl = resolveMediaUrl(rawUrl);
                  return (
                    <div key={photoItem?.id || pIdx} className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                      <div className="flex items-center justify-between text-xs font-bold text-slate-700">
                        <span>Photograph #{pIdx + 1}</span>
                      </div>
                      <div className="relative overflow-hidden rounded-md border border-slate-200 bg-slate-100">
                        {pUrl ? (
                          <img
                            src={pUrl}
                            alt={`Unsafe Act ${pIdx + 1}`}
                            onClick={() =>
                              setEnlargedImage({
                                src: pUrl,
                                label: `Unsafe Act / Condition #${pIdx + 1}`,
                              })
                            }
                            className="max-h-56 w-full object-contain cursor-zoom-in transition hover:opacity-90"
                          />
                        ) : (
                          <div className="flex h-36 items-center justify-center text-xs italic text-slate-400">
                            Photo unavailable
                          </div>
                        )}
                        <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white">
                          #{pIdx + 1}
                        </span>
                      </div>
                      {photoItem?.comment && (
                        <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded p-2">
                          <strong>Comment:</strong> {photoItem.comment}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : detail.photograph_of_unsafe_act ? (
              <div>
                <img
                  src={resolveMediaUrl(detail.photograph_of_unsafe_act)}
                  alt="Unsafe Act"
                  onClick={() =>
                    setEnlargedImage({
                      src: resolveMediaUrl(detail.photograph_of_unsafe_act),
                      label: "Unsafe Act / Condition",
                    })
                  }
                  className="max-w-full h-auto rounded-lg max-h-64 object-cover border border-slate-300 cursor-zoom-in transition hover:opacity-90"
                />
                {detail.photograph_of_unsafe_act_comment && (
                  <div className="mt-3 text-sm text-slate-700 bg-white border border-slate-200 rounded-lg p-3">
                    <strong>Comment:</strong>{" "}
                    {detail.photograph_of_unsafe_act_comment}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">
                No photograph attached
              </p>
            )}
          </div>

          {/* Q4 */}
          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 opacity-80">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-600">4. HAZARD/RISK</h3>
              <button
                type="button"
                onClick={() => setShowRiskMatrix(true)}
                className="text-xs font-bold bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-200 px-3 py-1.5 rounded-lg shadow-sm transition-colors"
              >
                View Risk Matrix
              </button>
            </div>
            <label className="text-xs text-slate-500 block mb-2">
              Hazard (Checkboxes)
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {HAZARDS_OPTIONS.map((opt) => {
                const isChecked = hazardsArray.some(
                  (h) => h === opt || h.startsWith(opt + ":"),
                );
                return (
                  <label
                    key={opt}
                    className="flex items-center gap-2 text-sm cursor-not-allowed text-slate-500"
                  >
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-slate-400 focus:ring-0 cursor-not-allowed"
                      checked={isChecked}
                      disabled
                    />
                    {opt}
                  </label>
                );
              })}
            </div>
            {hazardsArray.some((h) => h.startsWith("10. Other Hazards:")) && (
              <div className="mb-4">
                <label className="text-xs text-slate-500 block mb-1">
                  Other Hazard Details
                </label>
                <input
                  type="text"
                  className="w-full rounded-lg border-slate-300 p-2.5 text-sm bg-slate-100 cursor-not-allowed"
                  value={
                    hazardsArray
                      .find((h) => h.startsWith("10. Other Hazards:"))
                      ?.replace("10. Other Hazards: ", "") || ""
                  }
                  readOnly
                />
              </div>
            )}
            <div>
              <label className="text-xs text-slate-500 block mb-1">Risk</label>
              <input
                type="text"
                className={`w-full rounded-lg border p-2.5 text-sm cursor-not-allowed ${
                  detail.risk === "Low Risk"
                    ? "border-green-500 bg-green-50 text-green-700"
                    : detail.risk === "Medium Risk"
                      ? "border-yellow-500 bg-yellow-50 text-yellow-700"
                      : detail.risk === "High Risk"
                        ? "border-red-500 bg-red-50 text-red-700"
                        : "border-slate-300 bg-slate-100 text-slate-700"
                }`}
                value={detail.risk || ""}
                readOnly
              />
            </div>
          </div>

          {/* Q5 */}
          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 opacity-80">
            <h3 className="font-bold text-slate-600 mb-4">
              5. NAME OF CONTRACTOR
            </h3>
            <input
              type="text"
              className="w-full max-w-sm rounded-lg border-slate-300 p-2.5 text-sm bg-slate-100 cursor-not-allowed"
              value={detail.name_of_contractor || ""}
              readOnly
            />
          </div>

          {/* Q6 */}
          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 opacity-80">
            <h3 className="font-bold text-slate-600 mb-4">6. TARGET DATE</h3>
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">
                  Date
                </label>
                <input
                  type="date"
                  className={`rounded-lg border p-2.5 text-sm w-[160px] cursor-not-allowed ${
                    detail.risk === "Low Risk"
                      ? "border-green-500 bg-green-50 text-green-700"
                      : detail.risk === "Medium Risk"
                        ? "border-yellow-500 bg-yellow-50 text-yellow-700"
                        : detail.risk === "High Risk"
                          ? "border-red-500 bg-red-50 text-red-700"
                          : "border-slate-300 bg-slate-100 text-slate-700"
                  }`}
                  value={(detail.target_date || "").split("T")[0] || ""}
                  readOnly
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">
                  Time (hh:mm)
                </label>
                <input
                  type="time"
                  className={`rounded-lg border p-2.5 text-sm w-[130px] cursor-not-allowed ${
                    detail.risk === "Low Risk"
                      ? "border-green-500 bg-green-50 text-green-700"
                      : detail.risk === "Medium Risk"
                        ? "border-yellow-500 bg-yellow-50 text-yellow-700"
                        : detail.risk === "High Risk"
                          ? "border-red-500 bg-red-50 text-red-700"
                          : "border-slate-300 bg-slate-100 text-slate-700"
                  }`}
                  value={(
                    (detail.target_date || "").split("T")[1] || ""
                  ).substring(0, 5)}
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Q7 */}
          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 opacity-80">
            <h3 className="font-bold text-slate-600 mb-4">
              7. CA/PA TO BE TAKEN
            </h3>
            <div className="space-y-4">
              <div>
                <div
                  className="w-full rounded-lg border border-slate-300 p-3 text-sm bg-slate-200 cursor-not-allowed whitespace-pre-wrap min-h-[6rem]"
                  dangerouslySetInnerHTML={{
                    __html: detail.ca_pa_combined || "",
                  }}
                />
              </div>
            </div>
          </div>

          {previousAttempts.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
              <h3 className="mb-1 font-bold text-slate-700">
                PREVIOUS RECTIFICATION / REVIEW HISTORY
              </h3>
              <p className="mb-4 text-xs text-slate-500">
                Earlier Maker submissions and Checker decisions for this Observation.
              </p>

              <div className="space-y-4">
                {previousAttempts.map((attempt) =>
                  renderAttemptHistory(attempt),
                )}
              </div>
            </div>
          )}

          {/* Maker Input (Q8) */}
          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-orange-500"></div>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-bold text-slate-600">
                8. CLOSER PHOTOGRAPH
              </h3>
              <div className="text-right text-xs text-slate-500">
                <span className="font-semibold text-slate-700">
                  {submittedMakerName || "Maker"}
                </span>
                {" · "}
                {formatDateTime(makerSubmittedAt)}
              </div>
            </div>

            {shareOneCommonComment && makerSubmissionComment && (
              <div className="mb-4 whitespace-pre-wrap rounded-lg border border-orange-100 bg-orange-50 p-3 text-sm text-slate-700">
                <strong>Maker Remarks:</strong> {makerSubmissionComment}
              </div>
            )}

            {closerPhotographs.length === 0 ? (
              <p className="text-sm text-slate-500 italic">
                No closer photograph attached.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {closerPhotographs.map((photo, idx) => {
                  const photoPath = getPhotoPath(photo);
                  const showIndividualComment =
                    !shareOneCommonComment && photo.comment;

                  return (
                    <div
                      key={idx}
                      className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-700">
                          Photograph #{idx + 1}
                        </span>
                      </div>
                      <div className="h-40 w-full overflow-hidden rounded-lg bg-slate-100">
                        <img
                          src={resolveMediaUrl(photoPath)}
                          alt={`Closer photograph ${idx + 1}`}
                          onClick={() =>
                            setEnlargedImage({
                              src: resolveMediaUrl(photoPath),
                              label: `Closer Photograph ${idx + 1}`,
                            })
                          }
                          className="h-full w-full cursor-zoom-in object-contain transition hover:opacity-90"
                        />
                      </div>

                      {showIndividualComment && (
                        <div className="mt-2 text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-lg p-2.5">
                          <strong>Comment:</strong> {photo.comment}
                        </div>
                      )}

                      {(actionType === "reject" || actionType === "approve") && (
                        <div
                          className={`mt-3 rounded-lg border p-3 ${
                            actionType === "reject"
                              ? "border-red-200 bg-red-50"
                              : "border-green-200 bg-green-50/70"
                          }`}
                        >
                          <label
                            className={`text-xs font-semibold block mb-1 ${
                              actionType === "reject"
                                ? "text-red-700"
                                : "text-green-800"
                            }`}
                          >
                            Checker Comment for this Photo (Optional)
                          </label>
                          <input
                            type="text"
                            className={`w-full rounded-md text-sm p-2 bg-white ${
                              actionType === "reject"
                                ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                                : "border-green-300 focus:border-green-500 focus:ring-green-500"
                            }`}
                            placeholder={
                              actionType === "reject"
                                ? "e.g. This is blurry, or doesn't show the fix..."
                                : "e.g. Rectification verified and accepted on site..."
                            }
                            value={photoComments[photo.id] || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setPhotoComments((prev) => ({
                                ...prev,
                                [photo.id]: val,
                              }));
                              if (selectedFinalImage?.id === photo.id) {
                                setFinalReportCommentText(val);
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Dedicated Contractor Checker Review Card on Current Attempt */}
          {hasCurrentAttemptContractorReview(currentAttempt) && (
            <div className="mt-6">
              <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-emerald-800">
                Contractor Checker Review
              </h3>
              <ObservationReviewCard
                reviewerRole="Contractor Checker"
                reviewerName={currentAttempt.checker_name}
                decision={
                  String(currentAttempt.checker_decision).toUpperCase() === "APPROVED"
                    ? "APPROVED / FORWARDED"
                    : String(currentAttempt.checker_decision).toUpperCase()
                }
                remarks={currentAttempt.checker_reason}
                reviewedAt={currentAttempt.checker_reviewed_at}
                signature={currentAttempt.checker_signature}
                rejectionPhotos={
                  String(currentAttempt.checker_decision).toUpperCase() === "REJECTED"
                    ? currentAttempt.checker_reject_photographs
                    : []
                }
                onImageClick={(src, label) => setEnlargedImage({ src, label })}
              />
            </div>
          )}

          <SafetyObservationSignOffSection
            detail={detail}
            className="mt-6"
          />

          <div className="mt-8 pt-4 border-t border-slate-100">
            {actionType === "reject" ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 mb-4">
                <h4 className="font-semibold text-red-700 mb-2">
                  Reason for Rejection
                </h4>
                <textarea
                  className="w-full rounded-lg border-red-300 p-3 text-sm focus:border-red-500 focus:ring-red-500"
                  rows={3}
                  placeholder="Explain why this rectification is rejected..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />

                <div className="mt-4 border-t border-red-100 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-red-700 text-sm sm:text-base">
                      Attach Photos (Optional)
                    </h4>
                    {rejectPhotos.length > 0 && (
                      <span className="text-xs font-semibold text-red-700 bg-red-100 px-2.5 py-0.5 rounded-full border border-red-200">
                        {rejectPhotos.length} photo{rejectPhotos.length > 1 ? "s" : ""} attached
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col items-center justify-center w-full rounded-xl border-2 border-dashed border-red-200 bg-white py-6 px-4 transition-colors hover:bg-red-50/40 text-center">
                    <UploadCloud className="mb-2 h-8 w-8 text-red-400" />
                    <p className="mb-1 text-sm font-semibold text-slate-700">
                      Upload from gallery or take a photo
                    </p>
                    <p className="mb-4 text-xs text-slate-400">
                      PNG, JPG, JPEG (Select from gallery or take photo)
                    </p>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2.5 sm:gap-3 w-full sm:w-auto">
                      <label
                        htmlFor="reject-photo-gallery-upload"
                        className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-xs sm:text-sm font-semibold text-red-700 hover:bg-red-100 transition-colors shadow-2xs"
                      >
                        <UploadCloud className="h-4 w-4 text-red-600" />
                        <span>Select from Gallery</span>
                      </label>

                      <button
                        type="button"
                        onClick={() => setRejectCameraOpen(true)}
                        className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-xs sm:text-sm font-semibold text-white hover:bg-red-700 transition-colors shadow-2xs"
                      >
                        <Camera className="h-4 w-4" />
                        <span>Take Photo</span>
                      </button>
                    </div>

                    <input
                      id="reject-photo-gallery-upload"
                      type="file"
                      className="hidden"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        if (e.target.files?.length > 0) {
                          const newItems = Array.from(e.target.files).map(
                            (file, idx) => ({
                              id: `${Date.now()}_${idx}_${Math.random().toString(36).substring(2, 7)}`,
                              file,
                              annotatedFile: null,
                              comment: "",
                              previewUrl: URL.createObjectURL(file),
                            }),
                          );
                          setRejectPhotos((prev) => [...prev, ...newItems]);
                        }
                        e.target.value = null; // reset to allow selecting the same files again
                      }}
                    />

                    <input
                      id="reject-photo-camera-fallback"
                      type="file"
                      className="hidden"
                      accept="image/*"
                      capture="environment"
                      onChange={handleRejectGalleryChange}
                    />
                  </div>

                  {rejectPhotos.length > 0 && (
                    <div className="mt-4 space-y-3">
                      {rejectPhotos.map((photoItem, idx) => (
                        <div
                          key={photoItem.id || idx}
                          className="rounded-lg border border-red-200 bg-white p-3 shadow-sm flex flex-col gap-2"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="flex items-center gap-3 min-w-0">
                              <img
                                src={photoItem.previewUrl}
                                alt="Reject"
                                className="h-12 w-12 shrink-0 object-cover rounded-lg border border-slate-200"
                              />
                              <span className="text-sm font-medium text-slate-700 truncate max-w-[150px]">
                                {photoItem.file?.name}
                              </span>
                              {photoItem.annotatedFile && (
                                <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-700">
                                  Annotated
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                              <button
                                type="button"
                                onClick={() => {
                                  setAnnotatingIndex(idx);
                                  setShowAnnotationModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm font-semibold px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                              >
                                Annotate
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setRejectPhotos((prev) =>
                                    prev.filter((_, i) => i !== idx),
                                  )
                                }
                                className="text-red-600 hover:text-red-800 text-xs sm:text-sm font-semibold px-2 py-1 rounded hover:bg-red-50 transition-colors"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                          <input
                            type="text"
                            className="w-full rounded-md border border-slate-300 text-sm p-2 outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
                            placeholder="Optional comment..."
                            value={photoItem.comment || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setRejectPhotos((prev) => {
                                const newArr = [...prev];
                                newArr[idx] = { ...newArr[idx], comment: val };
                                return newArr;
                              });
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-4 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setActionType(null);
                      setRejectReason("");
                      setRejectPhotos([]);
                    }}
                    className="w-full sm:w-auto rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-red-100 transition-colors text-center"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleReject}
                    disabled={submitting}
                    className="w-full sm:w-auto rounded-lg bg-red-600 px-6 py-2 text-sm font-bold text-white shadow-sm hover:bg-red-700 disabled:opacity-50 transition-colors text-center"
                  >
                    {submitting ? "Rejecting..." : "Confirm Rejection"}
                  </button>
                </div>
              </div>
            ) : actionType === "approve" ? (
              <div className="rounded-xl border border-green-200 bg-green-50/70 p-5 mb-4 shadow-sm animate-in fade-in-50 duration-200">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h4 className="font-bold text-green-900">
                      Approve Observation Rectification
                    </h4>
                    <p className="text-xs text-green-700 mt-0.5">
                      You can provide approval remarks or comments below before completing the approval.
                    </p>
                  </div>
                  <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-bold text-green-800">
                    Approval Mode
                  </span>
                </div>

                <div className="mt-3">
                  <label className="text-xs font-semibold text-green-900 block mb-1.5">
                    Checker Approval Remarks / Comments (Optional)
                  </label>
                  <textarea
                    className="w-full rounded-lg border-green-300 bg-white p-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-green-500 focus:ring-green-500 shadow-inner"
                    rows={3}
                    placeholder="Enter approval remarks, verification details, or site notes (optional)..."
                    value={approvalRemarks}
                    onChange={(e) => setApprovalRemarks(e.target.value)}
                  />
                </div>

                <div className="mt-4 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setActionType(null);
                    }}
                    className="w-full sm:w-auto rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-green-100 transition-colors text-center"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={submitting}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-lg bg-green-600 px-6 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-green-700 disabled:opacity-50 transition-colors text-center"
                  >
                    <Check className="h-4 w-4 stroke-[3]" />
                    {submitting ? "Processing..." : isInternalCheckerReview ? "Proceed to Select Final Photo" : "Proceed to Sign & Approve"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-6">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setActionType("reject")}
                    disabled={submitting}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-500 px-4 py-3.5 text-sm font-bold text-white disabled:opacity-60 hover:bg-red-600 transition-colors shadow-sm"
                  >
                    <XIcon className="h-5 w-5" />
                    Reject
                  </button>
                  <button
                    type="button"
                    onClick={() => setActionType("approve")}
                    disabled={submitting}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-green-600 px-4 py-3.5 text-sm font-bold text-white disabled:opacity-60 hover:bg-green-700 transition-colors shadow-sm"
                  >
                    <Check className="h-5 w-5" />
                    Approve
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isSignatureModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-3 sm:p-4 backdrop-blur-sm">
          <div className="w-full max-w-md max-h-[94vh] flex flex-col animate-in zoom-in-95 rounded-2xl bg-white p-4 sm:p-5 shadow-xl my-auto overflow-y-auto">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">
                Final Signature
              </h3>
              <button
                type="button"
                onClick={() => {
                  sigPadRef.current?.clear();
                  setIsSignatureModalOpen(false);
                }}
                className="rounded p-1 text-muted-foreground hover:bg-muted"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>
            <p className="mb-2 text-xs text-muted-foreground">
              Draw your signature and {signatureAction === "approve" ? "approve" : "reject"} the observation.
            </p>
            {signatureAction === "approve" && (approvalRemarks || finalReportCommentText) && (
              <div className="mb-2.5 space-y-1 rounded-lg border border-green-200 bg-green-50 p-2.5 text-xs text-green-900">
                {approvalRemarks && (
                  <div>
                    <span className="font-bold">Checker Remarks: </span>
                    <span className="text-slate-700">{approvalRemarks}</span>
                  </div>
                )}
                {finalReportCommentText && (
                  <div>
                    <span className="font-bold">Final Report Comment: </span>
                    <span className="text-slate-700">{finalReportCommentText}</span>
                  </div>
                )}
              </div>
            )}
            <div className="rounded-lg border border-border bg-white p-1.5 sm:p-2">
              <SafetyResponsiveSignatureCanvas ref={sigPadRef} penColor="black" height={160} />
            </div>
            <div className="mt-2.5 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => sigPadRef.current?.clear()}
                className="w-full rounded-lg border border-slate-200 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => {
                  if (sigPadRef.current?.isEmpty()) {
                    showToast("Please provide your signature.", "error");
                    return;
                  }
                  const sigData = sigPadRef.current
                    .getCanvas()
                    .toDataURL("image/png");
                  fetch(sigData)
                    .then((res) => res.blob())
                    .then((blob) => {
                      const file = new File(
                        [blob],
                        `checker-signature-${Date.now()}.png`,
                        { type: "image/png" },
                      );
                      setIsSignatureModalOpen(false);
                      confirmSubmit(file);
                    });
                }}
                className={`w-full rounded-lg py-2.5 text-xs font-semibold text-white shadow-sm transition-all disabled:opacity-60 ${
                  signatureAction === "approve"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-600 hover:bg-red-700"
                }`}
                disabled={submitting}
              >
                {submitting
                  ? "Submitting..."
                  : signatureAction === "approve"
                    ? "Approve Observation"
                    : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}

      <RiskMatrixModal
        open={showRiskMatrix}
        onClose={() => setShowRiskMatrix(false)}
      />

      {showFinalImageModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-3 sm:p-4 backdrop-blur-xs overflow-y-auto">
          <div className="flex w-full max-w-2xl max-h-[92vh] animate-in zoom-in-95 flex-col overflow-hidden rounded-2xl bg-white shadow-2xl border border-slate-200 my-auto">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-100 px-4 sm:px-6 py-3.5 sm:py-4 bg-slate-50/50">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-800">
                  Select Final Image
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Select the Maker photograph to appear in the final report.
                </p>
              </div>
              <button
                onClick={() => setShowFinalImageModal(false)}
                className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                aria-label="Close"
              >
                <XIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(92vh-140px)] space-y-4 sm:space-y-6">
              <p className="text-xs sm:text-sm text-slate-500">
                Please select the Maker Closure Photograph that should appear in the final Safety Observation report. (Comments are optional).
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                {availableFinalImages.map((img, imgIdx) => {
                  const uniqueRemarks = extractUniqueRemarks(img);
                  const isSelected =
                    selectedFinalImage?.id === img.id &&
                    selectedFinalImage?.type === img.type;

                  return (
                    <div
                      key={`${img.type}-${img.id || imgIdx}`}
                      className="flex flex-col gap-2"
                    >
                      <div
                        onClick={() => {
                          setSelectedFinalImage(img);
                          const pComment = photoComments[img.id] || "";
                          setSelectedFinalComments(uniqueRemarks);
                          if (pComment) {
                            setFinalReportCommentText(pComment);
                          } else if (uniqueRemarks.length > 0) {
                            setFinalReportCommentText(uniqueRemarks.join("\n"));
                          } else if (!finalReportCommentText.trim() && approvalRemarks.trim()) {
                            setFinalReportCommentText(approvalRemarks.trim());
                          }
                        }}
                        className={`group relative cursor-pointer overflow-hidden rounded-xl border-2 transition-all duration-200 ${
                          isSelected
                            ? "border-green-500 shadow-md ring-2 ring-green-500/30 ring-offset-1"
                            : "border-slate-200 hover:border-green-300"
                        }`}
                      >
                        <div className="aspect-square bg-slate-50">
                          <img
                            src={resolveMediaUrl(getPhotoPath(img))}
                            alt={`Photograph ${img.photo_index || imgIdx + 1}`}
                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        </div>
                        <div className="absolute top-2 left-2 flex gap-1">
                          {isSelected && (
                            <span className="flex h-6 w-6 animate-in zoom-in items-center justify-center rounded-full bg-green-500 text-white shadow-sm">
                              <Check className="h-4 w-4 stroke-[3]" />
                            </span>
                          )}
                        </div>
                        <span className="absolute top-2 right-2 rounded-md bg-slate-900/80 px-2 py-0.5 text-[11px] font-bold text-white shadow-sm backdrop-blur-sm">
                          Photo #{img.photo_index || imgIdx + 1}
                        </span>
                        <div className="absolute bottom-0 w-full bg-slate-900/70 px-3 py-1.5 sm:py-2 backdrop-blur-sm">
                          <span className="text-xs font-semibold text-white/95">
                            Photograph #{img.photo_index || imgIdx + 1} · Attempt {img.attempt_no || "-"}
                          </span>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-700">
                        <div className="flex flex-wrap items-center justify-between gap-1">
                          <span className="font-bold text-slate-800 text-xs sm:text-sm">
                            Photograph #{img.photo_index || imgIdx + 1}
                          </span>
                          <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700">
                            Attempt {img.attempt_no || "-"}
                          </span>
                        </div>
                        <div className="mt-1 text-[11px] text-slate-500">
                          {formatDateTime(img.maker_submitted_at)}
                        </div>

                        <p className="mt-1 text-xs">
                          <span className="font-semibold">Submitted By:</span>{" "}
                          {img.maker_name || "Maker"}
                        </p>

                        {img.maker_submission_remark && (
                          <div className="mt-2 rounded-md border border-orange-100 bg-orange-50 p-2">
                            <p className="font-semibold text-orange-700 text-[11px]">
                              Maker Remark
                            </p>
                            <p className="mt-0.5 whitespace-pre-wrap text-slate-700 text-xs">
                              {img.maker_submission_remark}
                            </p>
                          </div>
                        )}

                        {img.photo_remark &&
                          img.photo_remark.trim().toLowerCase() !==
                            (img.maker_submission_remark || "").trim().toLowerCase() && (
                            <div className="mt-2 rounded-md border border-blue-100 bg-blue-50 p-2">
                              <p className="font-semibold text-blue-700 text-[11px]">
                                Photo Remark
                              </p>
                              <p className="mt-0.5 whitespace-pre-wrap text-slate-700 text-xs">
                                {img.photo_remark}
                              </p>
                            </div>
                          )}

                        {uniqueRemarks.length > 0 && (
                          <label className="mt-2 flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 bg-white p-2 hover:bg-slate-50 transition-colors">
                            <input
                              type="checkbox"
                              className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-orange-600"
                              checked={uniqueRemarks.every((rem) =>
                                selectedFinalComments.includes(rem),
                              )}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedFinalComments((prev) => [
                                    ...new Set([...prev, ...uniqueRemarks]),
                                  ]);
                                  setFinalReportCommentText((prev) => {
                                    const prevLines = prev ? prev.split("\n") : [];
                                    return [...new Set([...prevLines, ...uniqueRemarks])].join("\n");
                                  });
                                } else {
                                  setSelectedFinalComments((prev) =>
                                    prev.filter((c) => !uniqueRemarks.includes(c)),
                                  );
                                  setFinalReportCommentText((prev) => {
                                    return prev
                                      .split("\n")
                                      .filter((c) => !uniqueRemarks.includes(c))
                                      .join("\n");
                                  });
                                }
                              }}
                            />
                            <span className="text-[11px] text-slate-600">
                              Include in final report comment:{" "}
                              <span className="font-medium text-slate-800">
                                {uniqueRemarks.join(" · ")}
                              </span>
                            </span>
                          </label>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Final Report Photograph Comment */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
                <label className="text-xs font-bold text-slate-700 block mb-1.5">
                  Final Report Photograph Comment (Optional)
                </label>
                <textarea
                  className="w-full rounded-lg border border-slate-300 bg-white p-2.5 text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:border-green-500 focus:ring-green-500 outline-none"
                  rows={2}
                  placeholder="Enter comments to be displayed alongside this photograph in the final report..."
                  value={finalReportCommentText}
                  onChange={(e) => setFinalReportCommentText(e.target.value)}
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  This comment will be saved as the photograph's final verification remark in the inspection report.
                </p>
              </div>

              {/* Checker Approval Remarks in Modal */}
              <div className="rounded-xl border border-green-200 bg-green-50/60 p-3 sm:p-4">
                <label className="text-xs font-bold text-green-900 block mb-1.5">
                  Checker Approval Remarks (Optional)
                </label>
                <textarea
                  className="w-full rounded-lg border border-green-300 bg-white p-2.5 text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:border-green-500 focus:ring-green-500 outline-none"
                  rows={2}
                  placeholder="Enter overall checker remarks on the observation..."
                  value={approvalRemarks}
                  onChange={(e) => setApprovalRemarks(e.target.value)}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2.5 sm:gap-3 px-4 sm:px-6 py-3.5 sm:py-4 border-t border-slate-100 bg-slate-50/50">
              <button
                onClick={() => setShowFinalImageModal(false)}
                className="w-full sm:w-auto rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 text-center"
              >
                Cancel
              </button>
              <button
                onClick={confirmFinalImageSelection}
                disabled={!selectedFinalImage}
                className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl bg-green-600 px-6 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-green-700 disabled:opacity-50 text-center"
              >
                <Check className="h-4 w-4 stroke-[3]" />
                Confirm Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Camera Capture Modal */}
      <SafetyCameraCaptureModal
        open={rejectCameraOpen}
        title="Take Rejection Photo"
        onClose={() => setRejectCameraOpen(false)}
        onCapture={handleRejectCameraCapture}
      />

      {showAnnotationModal &&
        annotatingIndex !== null &&
        rejectPhotos[annotatingIndex] && (
          <SafetyImageAnnotationModal
            open={showAnnotationModal}
            file={
              rejectPhotos[annotatingIndex].annotatedFile ||
              rejectPhotos[annotatingIndex].file
            }
            onSave={(annotatedFile) => {
              const newFiles = [...rejectPhotos];
              newFiles[annotatingIndex] = {
                ...newFiles[annotatingIndex],
                annotatedFile,
                previewUrl: URL.createObjectURL(annotatedFile),
              };
              setRejectPhotos(newFiles);
              setShowAnnotationModal(false);
              setAnnotatingIndex(null);
            }}
            onClose={() => {
              setShowAnnotationModal(false);
              setAnnotatingIndex(null);
            }}
          />
        )}

      {/* Enlarged image lightbox (view full-size submitted photo) */}
      {enlargedImage && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={() => setEnlargedImage(null)}
        >
          <button
            type="button"
            onClick={() => setEnlargedImage(null)}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
          >
            <XIcon className="h-6 w-6" />
          </button>
          <div
            className="flex max-h-[90vh] max-w-[90vw] flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={enlargedImage.src}
              alt={enlargedImage.label || "Enlarged photograph"}
              className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl"
            />
            {enlargedImage.label && (
              <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white">
                {enlargedImage.label}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}